/* eslint-disable complexity */
import React, {
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Loader } from '@unmind/design-system';
import { useInfiniteScroll, Direction } from 'hooks';
import { DropdownMenu, MenuItem } from 'Navigation/AccountMenu/DropdownMenu';
import { MenuItemConfig } from 'Navigation/AccountMenu/AccountMenu';
import { Trash, Message, NovaInfo } from 'icons';
import { useUserFeedbackQuery } from 'Shared/FeedbackBanner/useUserFeedbackQuery';
import { getUserLocale } from 'i18n/getUserLocale';
import { FEEDBACK_LOCALES } from 'i18n/feedbackLocales';
import { isMSTeams } from 'utils/MSTeams/MSTeamsHelpers';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { ChatbotKey } from 'Assistant/__generated__/client.schemas';
import type {
  ChatbotMessageSentSource,
  ConversationUpdateType,
  ScrollLocation,
  UserMessage,
} from '../types';
import {
  useConversationHistory,
  useSubmitInitialMessage,
  useIsScrollAtBottom,
  useSendUrlPromptMessage,
} from '../hooks';
import { messagesToConversationSessions } from '../utils';
import { useAuth0Token } from '../hooks/useAuth0Token';
import { ChatbotInput } from './ChatbotInput';
import { ChatbotSession } from './ChatbotSession';
import {
  ChatbotFooter,
  ChatBotFooterText,
  ChatBotInputContainer,
  ChatbotMessageWrapper,
  ChatbotWrapper,
  LoadingMoreErrorWrapper,
  LoadingMoreWrapper,
  FeatureUnavailableErrorWrapper,
  ScrollIconContainer,
  ScrollIcon,
  SettingsMenuWrapper,
  SettingsIcon,
  SettingsMenuItem,
  ClearChatLabel,
  ChatbotFooterContent,
} from './styles';
import { Error } from './Error';
import { ClearChatModal } from './ClearChatModal';
import { getFeedbackFormLink } from './ChatbotFeedback/utils';
import { AboutNovaModal } from './AboutNovaModal';
import { TransparencyMessage } from './TransparencyMessage';
import { FeedbackRequestBanner as VoiceFeedbackRequestBanner } from './Voice';

const FEEDBACK_SURVEY_LINK = 'https://unmind.typeform.com/to/ISVJNakZ';

const CONVERSATION_UPDATE_SCROLL_MAPPING: Record<
  ConversationUpdateType,
  ScrollLocation
> = {
  NEW_MESSAGE: 'BOTTOM',
  LOAD_MORE: 'CURRENT',
};

/*
 * userIntent: Conversation starts due to user intent, like sending a message from the home screen / clicking a conversation starter / clicking a deep link with a URL prompt.
 * nova: Nova initiates a conversation based on the user's chat history or lack thereof. E.g. welcome conversation or follow up.
 */
export type ConversationStartMode = 'userIntent' | 'nova';

export interface ChatbotProps {
  /**
   * An optional initial message from landing page redirect
   */
  initialMessage?: UserMessage;
  initialMessageSource?: ChatbotMessageSentSource;
  showVoiceFeedback?: boolean;
  /**
   * HMAC-signed URL prompt params from email deep links. When both are
   * present the conversation is initiated via a `url_prompt` command.
   */
  np?: string;
  sig?: string;
}

const redirectToSignIn = () => {
  const SIGNIN_PATH = '/?redirect=';
  const { origin, pathname, search, hash } = window.location;
  const redirectTarget = encodeURIComponent(`${pathname}${search}${hash}`);
  window.location.assign(`${origin}${SIGNIN_PATH}${redirectTarget}`);
};

export const Chatbot = ({
  initialMessage,
  initialMessageSource,
  showVoiceFeedback,
  np,
  sig,
}: ChatbotProps) => {
  const theme = useTheme();
  const { t: translate } = useTranslation('chatbot');
  const [shouldSubmitInitialMessage, setSubmitInitialMessage] =
    useSubmitInitialMessage();
  const { isScrollAtBottom, resetScrollAtBottom } = useIsScrollAtBottom(500);
  const [hasSentFollowUp, setHasSentFollowUp] = useState(false);
  const [clearChatModalOpen, setClearChatModalOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [aboutNovaModalOpen, setAboutNovaModalOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const userLocale = getUserLocale();
  const isInMSTeams = isMSTeams();

  const toggleSettingsMenu = () => {
    setSettingsMenuOpen(!settingsMenuOpen);
  };

  const closeSettingsMenu = () => {
    setSettingsMenuOpen(false);
  };

  const { data } = useUserFeedbackQuery();
  const feedbackFormLink = getFeedbackFormLink(FEEDBACK_SURVEY_LINK, data);

  const [currentHeight, setCurrentHeight] = useState(
    document.body.scrollHeight,
  );
  const [scrollAction, setScrollAction] = useState<ScrollLocation>('BOTTOM');

  const pageRef: MutableRefObject<null> = useRef(null);

  function scrollToEnd() {
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight || window.innerHeight,
      });
    }, 10);
  }

  const handleConversationUpdated: Parameters<
    typeof useConversationHistory
  >[0]['onConversationUpdated'] = updateType => {
    setScrollAction(CONVERSATION_UPDATE_SCROLL_MAPPING[updateType]);
  };

  const welcomeConversationEnabled = useFeatureFlag(
    FEATURE_FLAGS.NOVA_WEB_WELCOME_CONVERSATION,
  );

  // Navigation intent is fixed for the whole chat session: arriving with a
  // user intent (conversation starter / home chat input / url-prompt deep link) pins
  // the Default chatbot for every message; arriving with no user intent leaves it
  // unset so the Chat API can choose the initiator (Welcome Conversation or
  // generic follow-up). Frozen in a ref so stripping np/sig from the URL
  // mid-session (see useSendUrlPromptMessage) can't flip it.
  const conversationStartMode: ConversationStartMode =
    Boolean(initialMessage) || Boolean(np && sig) ? 'userIntent' : 'nova';
  const conversationStartModeRef = useRef(conversationStartMode);
  // When the welcome conversation is disabled we pin the Default chatbot for
  // every message (the pre-flag behaviour), which stops the server picking the
  // Welcome Conversation initiator.
  const chatModelId =
    !welcomeConversationEnabled ||
    conversationStartModeRef.current === 'userIntent'
      ? ChatbotKey.default
      : undefined;

  const {
    messages,
    conversationId,
    isSubmittingMessage,
    isStreamingMessage,
    isLoadingHistory,
    hadErrorSubmittingMessage,
    hadErrorLoadingHistory,
    transparencyCheckRequired,
    submitMessage,
    resubmitMessages,
    loadMoreMessages,
    retryLoadingHistory,
    submitFollowUpMessage,
    submitUrlPromptMessage,
    handleAcknowledgeTransparencyCheck,
    clearConversationHistory,
  } = useConversationHistory({
    onConversationUpdated: handleConversationUpdated,
    chatModelId,
    welcomeConversationEnabled,
    hasUserIntent: conversationStartModeRef.current === 'userIntent',
  });

  useSendUrlPromptMessage({
    np,
    sig,
    isLoadingHistory,
    transparencyCheckRequired,
    submitUrlPromptMessage,
  });

  useEffect(() => {
    if (!isLoadingHistory && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoadingHistory, isInitialLoad]);

  const handleClearChat = () => {
    resetScrollAtBottom();
    setClearChatModalOpen(false);
    clearConversationHistory();
    // Reset so a follow-up is sent again once the re-shown transparency check
    // is acknowledged in the same session.
    setHasSentFollowUp(false);
    // A cleared conversation is a fresh, intent-less start: drop the pinned
    // Default chatbot so the follow-up (and subsequent messages) omit
    // chat_model_id and let the Chat API choose the initiator.
    conversationStartModeRef.current = 'nova';
  };

  const settingsMenuItemsConfig: MenuItemConfig[] = [
    {
      key: 'about-nova',
      label: (
        <SettingsMenuItem>
          <NovaInfo />
          {translate('modals.about_nova.title')}
        </SettingsMenuItem>
      ),
      onSelect: () => setAboutNovaModalOpen(true),
    },
    {
      key: 'share-feedback',
      label: (
        <SettingsMenuItem>
          <Message />
          <a href={feedbackFormLink} target="_blank" className="no-decoration">
            {translate('modals.settings.feedback_button.label')}
          </a>
        </SettingsMenuItem>
      ),
      isEnabled: FEEDBACK_LOCALES.includes(userLocale),
    },
    {
      key: 'clear-chat',
      label: (
        <SettingsMenuItem>
          <Trash />
          <ClearChatLabel>
            {translate('modals.settings.clear_chat_button.label')}
          </ClearChatLabel>
        </SettingsMenuItem>
      ),
      onSelect: () => setClearChatModalOpen(true),
      isEnabled: messages.length !== 0,
    },
  ];

  const onOptionSelected = (menuItem: MenuItemConfig) => () => {
    const { onSelect } = menuItem;

    if (onSelect) {
      onSelect();
    }
  };

  const menuItems = settingsMenuItemsConfig
    .filter(({ isEnabled }) => isEnabled !== false)
    .map(menuItem => (
      <MenuItem
        data-testid={`menu-item-${menuItem.key}`}
        aria-label={menuItem.label}
        key={menuItem.key}
        label={menuItem.label}
        onClick={onOptionSelected(menuItem)}
        isInNovaLUI={true}
        {...menuItem.overrides}
      />
    ));

  const sessions = useMemo(
    () => messagesToConversationSessions(messages),
    [messages],
  );

  const isLatestSectionEmpty =
    sessions.length > 0 && sessions[sessions.length - 1].messages.length === 0;

  // With the welcome conversation enabled, Nova greets on every intent-less
  // navigation, including brand-new users with no history. With it disabled we
  // revert to the pre-flag behaviour: only returning users with an empty latest
  // session get a follow-up, so no-history users see no welcome conversation.
  const shouldSendFollowUp =
    conversationStartModeRef.current === 'nova' &&
    (welcomeConversationEnabled ||
      (messages.length !== 0 && isLatestSectionEmpty));

  /**
   * This effect handles a initial message from the home page redirect
   * It will submit the message if the history is loaded and the initial message
   * is set and if the shouldSubmitInitialMessage is true, the latter of the conditionals
   * prevents it from resubmitting the initial message on page reload.
   */
  useEffect(() => {
    if (
      initialMessage &&
      shouldSubmitInitialMessage &&
      !isLoadingHistory &&
      !transparencyCheckRequired
    ) {
      void setSubmitInitialMessage(false);
      void submitMessage(initialMessage, initialMessageSource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingHistory,
    initialMessage,
    initialMessageSource,
    shouldSubmitInitialMessage,
    transparencyCheckRequired,
  ]);

  useEffect(() => {
    // Fire a follow-up when the session should initiate one (see
    // shouldSendFollowUp, which also honours the welcome-conversation flag).
    // Gated on the transparency check so it never fires before the disclaimer
    // is acknowledged, and on hasSentFollowUp so it fires once per navigation.
    if (
      shouldSendFollowUp &&
      !transparencyCheckRequired &&
      !isStreamingMessage &&
      !isSubmittingMessage &&
      !isLoadingHistory &&
      !hasSentFollowUp
    ) {
      setHasSentFollowUp(true);
      void submitFollowUpMessage();
    }
  }, [
    shouldSendFollowUp,
    hasSentFollowUp,
    isLoadingHistory,
    isStreamingMessage,
    isSubmittingMessage,
    transparencyCheckRequired,
    submitFollowUpMessage,
  ]);
  /**
   * This useEffect hook handles the scroll position when new messages are added to the chat
   * and historical messages are loaded via infinite scroll.
   */
  useEffect(() => {
    if (sessions && sessions.length > 0) {
      if (scrollAction === 'BOTTOM') {
        scrollToEnd();
        setCurrentHeight(document.body.scrollHeight);
      } else if (scrollAction === 'CURRENT') {
        setTimeout(() => {
          // Calculate new scroll position
          const newHeight = document.body.scrollHeight;

          const newScrollPosition = newHeight - currentHeight;

          // Restore the scroll position
          window.scrollTo(0, newScrollPosition);
          setCurrentHeight(newHeight);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  useInfiniteScroll(
    () => {
      setTimeout(() => {
        void loadMoreMessages();
      }, 500);
    },
    pageRef,
    Direction.UP,
  );

  /**
   * If the user's session has expired or Chat API returns 401, redirect to sign in
   */
  const auth0Token = useAuth0Token();
  const unmindAuthToken = localStorage.getItem('token');
  if (auth0Token !== undefined) {
    if (
      !(auth0Token || unmindAuthToken) ||
      hadErrorLoadingHistory === 'USER_SESSION_EXPIRED'
    ) {
      redirectToSignIn();
    }
  }

  if (hadErrorLoadingHistory === 'FEATURE_UNAVAILABLE') {
    return (
      <ChatbotWrapper>
        <FeatureUnavailableErrorWrapper>
          <Error
            title={translate('conversation.something_went_wrong.title')}
            body={translate('conversation.something_went_wrong.body')}
            orientation="vertical"
          />
        </FeatureUnavailableErrorWrapper>
      </ChatbotWrapper>
    );
  }

  if (
    transparencyCheckRequired &&
    !isLoadingHistory &&
    !hadErrorLoadingHistory
  ) {
    return (
      <ChatbotWrapper>
        <TransparencyMessage
          handleAcknowledgeButtonPress={handleAcknowledgeTransparencyCheck}
        />
        <SettingsMenuWrapper>
          <DropdownMenu
            aria-label={translate('conversation.open_settings_button.label')}
            a11y-role="button"
            onClick={toggleSettingsMenu}
            open={settingsMenuOpen}
            onRequestClose={closeSettingsMenu}
            icon={<SettingsIcon />}
            isInNovaLUI={true}
            isInVerticalMenu={false}
          >
            {menuItems}
          </DropdownMenu>
        </SettingsMenuWrapper>
        {aboutNovaModalOpen && (
          <AboutNovaModal onClose={() => setAboutNovaModalOpen(false)} />
        )}
        {clearChatModalOpen && (
          <ClearChatModal
            conversationId={conversationId}
            onClose={() => {
              setClearChatModalOpen(false);
            }}
            onClearChat={handleClearChat}
            redirectToSignIn={redirectToSignIn}
          />
        )}
      </ChatbotWrapper>
    );
  }

  return (
    <ChatbotWrapper>
      {isLoadingHistory && isInitialLoad ? (
        <LoadingMoreWrapper
          aria-label={translate('conversation.loading.a11y_label')}
        >
          <Loader
            color={theme.colors.chatbot.messages.loading.bubbles}
            size={4}
          />
        </LoadingMoreWrapper>
      ) : hadErrorLoadingHistory ? (
        <LoadingMoreErrorWrapper>
          <Error
            title={translate(
              'conversation.conversation_errors.fetch_conversation_history_error.title',
            )}
            onTryAgainPress={retryLoadingHistory}
          />
        </LoadingMoreErrorWrapper>
      ) : (
        <>
          <ChatbotMessageWrapper>
            {isLoadingHistory && !isInitialLoad && (
              <LoadingMoreWrapper
                aria-label={translate('conversation.loading.a11y_label')}
              >
                <Loader
                  color={theme.colors.chatbot.messages.loading.bubbles}
                  size={4}
                />
              </LoadingMoreWrapper>
            )}
            {sessions &&
              sessions.map(({ date, messages: sessionMessages }, index) => (
                <ChatbotSession
                  key={date.toISOString().split('T')[0]}
                  isCurrentOldestLoadedSession={
                    index === 0 && sessions.length >= 1
                  }
                  date={date}
                  messages={sessionMessages}
                  loading={index === sessions.length - 1 && isSubmittingMessage}
                  error={
                    index === sessions.length - 1 && hadErrorSubmittingMessage
                  }
                  onSubmit={submitMessage}
                  onReSubmit={resubmitMessages}
                  hasInitialMessage={Boolean(initialMessage)}
                />
              ))}
          </ChatbotMessageWrapper>

          <ChatbotFooter>
            <VoiceFeedbackRequestBanner defaultVisibility={showVoiceFeedback} />
            {!isScrollAtBottom && messages.length > 1 ? (
              <ScrollIconContainer
                a11y-role="button"
                aria-label={translate(
                  'conversation.scroll_to_bottom.a11y_label',
                )}
                onClick={() => {
                  setScrollAction('BOTTOM');
                  scrollToEnd();
                }}
              >
                <ScrollIcon />
              </ScrollIconContainer>
            ) : null}
            <ChatbotFooterContent $isMSTeams={isInMSTeams}>
              <ChatBotInputContainer>
                <ChatbotInput
                  canSubmit={
                    !isSubmittingMessage &&
                    !isLoadingHistory &&
                    !isStreamingMessage
                  }
                  onSubmit={submitMessage}
                />
              </ChatBotInputContainer>
              <ChatBotFooterText>
                {translate('conversation.conversation_footer_text')}
              </ChatBotFooterText>
            </ChatbotFooterContent>
          </ChatbotFooter>
        </>
      )}

      <SettingsMenuWrapper>
        <DropdownMenu
          aria-label={translate('conversation.open_settings_button.label')}
          a11y-role="button"
          onClick={toggleSettingsMenu}
          open={settingsMenuOpen}
          onRequestClose={closeSettingsMenu}
          icon={<SettingsIcon />}
          isInNovaLUI={true}
          isInVerticalMenu={false}
        >
          {menuItems}
        </DropdownMenu>
      </SettingsMenuWrapper>
      {aboutNovaModalOpen && (
        <AboutNovaModal onClose={() => setAboutNovaModalOpen(false)} />
      )}
      {clearChatModalOpen && (
        <ClearChatModal
          conversationId={conversationId}
          onClose={() => {
            setClearChatModalOpen(false);
          }}
          onClearChat={handleClearChat}
          redirectToSignIn={redirectToSignIn}
        />
      )}
    </ChatbotWrapper>
  );
};
